// src/pages/SetupScreen.js
import React, { useState } from 'react';

const SetupScreen = ({ onComplete, userName, setVaultFiles }) => {
  const [dragActive, setDragActive] = useState(false);
  const [localFiles, setLocalFiles] = useState([]);
  const [error, setError] = useState('');

  // ─── Drag events ───
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // ─── Drop ───
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // ─── File input ───
  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);

    // Update local preview
    setLocalFiles((prev) => [...prev, ...fileArray]);

    // Update global app state so they appear in Vault
    if (typeof setVaultFiles === 'function') {
      setVaultFiles((prev) => [...prev, ...fileArray]);
    }

    setError('');
    console.log('[SetupScreen] Files queued:', fileArray.map((f) => f.name));
  };

  const handleRemoveFile = (index) => {
    setLocalFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── Continue ───
  const handleContinue = () => {
    if (localFiles.length === 0) {
      setError('Add files to continue, or skip to the dashboard.');
    } else {
      onComplete();
    }
  };

  return (
    <div className="setup-container fade-in">
      <div className="setup-card">
        <h1 style={{ margin: '0 0 6px 0', color: '#0f172a' }}>
          Welcome, {userName || 'User'}
        </h1>
        <p
          style={{
            margin: '0 0 24px 0',
            color: '#64748b',
            fontSize: '0.92rem',
          }}
        >
          Upload reference documents or templates to get started. You can
          always add more later from the Sono Vault.
        </p>

        {/* Upload zone */}
        <div
          className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-upload').click()}
          style={{
            border: dragActive
              ? '2px dashed #3b82f6'
              : '2px dashed #cbd5e1',
            background: dragActive ? '#eff6ff' : '#f8fafc',
            borderRadius: 12,
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: 16,
          }}
        >
          <input
            type="file"
            id="file-upload"
            multiple
            onChange={handleChange}
            style={{ display: 'none' }}
          />
          <div
            style={{
              fontSize: '2rem',
              marginBottom: 8,
            }}
          >
            📄
          </div>
          <p
            style={{
              margin: '0 0 4px 0',
              color: '#0f172a',
              fontSize: '0.95rem',
              fontWeight: 600,
            }}
          >
            Drag &amp; drop PDF, DOCX, or TXT files here
          </p>
          <small style={{ color: '#64748b' }}>or click to browse</small>
        </div>

        {/* File list */}
        {localFiles.length > 0 && (
          <div
            style={{
              textAlign: 'left',
              margin: '12px 0 16px 0',
              maxHeight: 140,
              overflowY: 'auto',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: '10px 12px',
            }}
          >
            <strong
              style={{
                display: 'block',
                fontSize: '0.82rem',
                color: '#475569',
                marginBottom: 6,
              }}
            >
              Ready to upload ({localFiles.length}):
            </strong>
            <ul
              style={{
                fontSize: '0.85rem',
                color: '#334155',
                paddingLeft: 18,
                margin: 0,
              }}
            >
              {localFiles.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '2px 0',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '80%',
                    }}
                  >
                    {file.name}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(index);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      padding: 0,
                    }}
                    aria-label="Remove file"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p
          className="ai-desc"
          style={{
            fontSize: '0.82rem',
            color: '#64748b',
            margin: '0 0 12px 0',
            textAlign: 'center',
          }}
        >
          These documents will be stored in the <strong>Sono Vault</strong>.
        </p>

        {/* Error */}
        {error && (
          <div
            style={{
              color: '#ef4444',
              fontSize: '0.85rem',
              marginBottom: 10,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              padding: '8px 12px',
              borderRadius: 6,
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <button
          className="btn-primary"
          onClick={handleContinue}
          style={{ width: '100%', padding: '11px', fontSize: '0.92rem' }}
        >
          {localFiles.length > 0 ? 'Upload & Continue' : 'Continue'}
        </button>

        <button
          className="btn-secondary"
          onClick={onComplete}
          style={{
            width: '100%',
            marginTop: 10,
            padding: '10px',
            fontSize: '0.88rem',
            border: '1px solid #cbd5e1',
            background: 'white',
            color: '#475569',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Skip to dashboard
        </button>
      </div>
    </div>
  );
};

export default SetupScreen;